import { useState } from "react";
import { Check, Copy, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/frontend/components/ui/sheet";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import { Label } from "@/frontend/components/ui/label";
import { useUpdatePatientDetailsMutation } from "@/frontend/mutations/dashboard/patients";
import { formatDateTime } from "@/frontend/utils/shared";
import { SELF_PATIENT_EXTERNAL_ID } from "@/server/constants/individual-workspace";
import type { PatientSummary } from "@/server/schema/admin";

/**
 * One label/value field in the details card. The label sits above its value
 * rather than across from it, so a field keeps its pairing at any column width
 * instead of stretching label and value to opposite edges.
 */
function DetailField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <dt className="flex items-center gap-1.5 text-caption text-muted-foreground">
        {label}
      </dt>
      <dd className="min-w-0 text-body font-semibold">{children}</dd>
    </div>
  );
}

/**
 * Patient details, the dominant card at the top of Summary, with an Edit sheet
 * that saves name and communication email through `updatePatientDetails`.
 * `canEdit` mirrors the procedure's org-admin gate. Date of birth and sex are
 * left out, since they come from records and read blank until a pull happens.
 */
export function PatientDetailsCard({
  summary,
  canEdit,
}: {
  summary: PatientSummary;
  canEdit: boolean;
}) {
  const [editOpen, setEditOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [copied, setCopied] = useState(false);

  const update = useUpdatePatientDetailsMutation(() => {
    toast.success("Patient details saved");
    setEditOpen(false);
  });

  /** Copies the API-facing patient id, flashing a check for confirmation. */
  const copyPatientId = async () => {
    await navigator.clipboard.writeText(summary.externalId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  /**
   * Opens the edit sheet with the current values as the draft. The email draft
   * comes from `communicationEmail` and never `row.email`, which falls back to
   * the synthetic `<uuid>@patient.internal` login address that must not be
   * persisted by an untouched save.
   */
  const openEdit = () => {
    setName(summary.row.name);
    setEmail(summary.communicationEmail ?? "");
    setEditOpen(true);
  };

  const canSave = name.trim().length > 0 && !update.isPending;
  const isOwnSummary = summary.externalId === SELF_PATIENT_EXTERNAL_ID;

  return (
    <section className="shrink-0 overflow-hidden rounded-xl border bg-card">
      <div className="flex h-10 items-center justify-between gap-2 border-b px-4">
        <h2 className="text-meta font-semibold uppercase tracking-wide text-foreground">Patient details</h2>
        {canEdit && (
          <Button
            variant="ghost"
            size="icon"
            className="-mr-2 size-8 text-muted-foreground"
            onClick={openEdit}
            aria-label="Edit patient details"
          >
            <Pencil className="size-4" />
          </Button>
        )}
      </div>

      <dl className="grid grid-cols-1 gap-x-8 gap-y-6 px-4 py-6 sm:grid-cols-2 lg:grid-cols-3">
        <DetailField label="Full name">
          <span className="block truncate" data-patient>
            {summary.row.name}
          </span>
        </DetailField>
        <DetailField label="Email">
          <span className="block truncate" data-patient>
            {summary.communicationEmail ?? "-"}
          </span>
        </DetailField>
        <DetailField label="Created">{formatDateTime(summary.row.createdAt)}</DetailField>
        {!isOwnSummary && (
          <DetailField label="Patient id">
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="truncate font-mono text-caption text-muted-foreground">
                {summary.externalId}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="-my-1 size-8 shrink-0 p-0"
                onClick={copyPatientId}
                aria-label="Copy patient id"
              >
                {copied ? (
                  <Check className="size-3 text-emerald-600 dark:text-emerald-400" />
                ) : (
                  <Copy className="size-3" />
                )}
              </Button>
            </span>
          </DetailField>
        )}
      </dl>

      <Sheet open={editOpen} onOpenChange={setEditOpen}>
        <SheetContent side="right" className="w-full gap-0 sm:max-w-sm">
          <SheetHeader>
            <SheetTitle>Edit patient details</SheetTitle>
          </SheetHeader>

          <form
            id="edit-patient-form"
            className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4 pb-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (!canSave) return;
              update.mutate(
                {
                  patientMembershipId: summary.row.id,
                  name: name.trim(),
                  communicationEmail: email.trim() === "" ? null : email.trim(),
                },
                {
                  onError: (error) => {
                    toast.error(
                      error instanceof Error ? error.message : "Could not save patient details",
                    );
                  },
                },
              );
            }}
          >
            <div className="flex flex-col gap-2">
              <Label htmlFor="edit-patient-name" className="text-body font-medium">
                Full name
              </Label>
              <Input
                id="edit-patient-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                className="h-8 text-body"
                autoFocus
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="edit-patient-email" className="text-body font-medium">
                Email
              </Label>
              <Input
                id="edit-patient-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="name@example.com"
                className="h-8 text-body"
              />
              <p className="text-caption text-muted-foreground">Email for any communication.</p>
            </div>
          </form>

          <SheetFooter className="mt-auto border-t">
            <div className="flex w-full justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                className="h-8"
                onClick={() => setEditOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" form="edit-patient-form" disabled={!canSave} className="h-8 gap-1.5">
                {update.isPending && <Loader2 className="size-4 animate-spin" />}
                Save changes
              </Button>
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </section>
  );
}
